import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, useMap, ZoomControl } from 'react-leaflet';
import L, { LatLngTuple, Map } from 'leaflet';
// import 'leaflet/dist/leaflet.css';
import styles from './styles_map.module.css';
import {
  TransportRegisterByCategoryResponse,
  TransportRegisterByTypeResponse,
  TransportTypeData,
  YearCategory
} from '../api/types';
import { ResponseDataGet } from '../../../../models/responseDataGet';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { EntTerritorialsUnits } from '../../../../models/handBooks/territorialsUnits';

interface EquipmentItem {
  name: string;
  count: number;
  icon: string;
}

interface MachineryMapProps {
  latitude: number;
  longitude: number;
  categoryData?: TransportRegisterByCategoryResponse;
  typeData?: TransportRegisterByTypeResponse;
  isLoading: boolean;
  polygonsData?: ResponseDataGet<EntTerritorialsUnits>;
  onRegionClick: (regionId: number, regionName: string) => void;
  selectedRegionName: string | null;
  initialRegionId?: number;
}

const getEquipmentData = (yearData: YearCategory): EquipmentItem[] => {
  if (!yearData.categories) {
    console.warn('No categories found in year data');
    return [];
  }

  const result = yearData.categories.flatMap(category =>
    category.agriculturalMachineryTypeQuantityAnalytics.map(analytics => ({
      name: analytics.transportType.nameRu,
      count: analytics.transportTypeQuantity,
      icon: ''
    }))
  ).filter(item => item.count > 0);


  return result;
};

const MapComponent: React.FC<{
  polygonsData?: ResponseDataGet<EntTerritorialsUnits>;
  onRegionClick: (regionId: number, regionName: string) => void;
  onPolygonLayersInit: (layers: { [key: string]: L.Polygon }) => void;
  initialRegionId?: number;
}> = ({ polygonsData, onRegionClick, onPolygonLayersInit, initialRegionId }) => {
  const map = useMap();
  const polygonLayersRef = useRef<{ [key: string]: L.Polygon }>({});
  const activePolygonRef = useRef<L.Polygon | null>(null);

  const setActivePolygonStyle = useCallback((polygonLayer: L.Polygon) => {
    if (activePolygonRef.current !== polygonLayer) {
      Object.values(polygonLayersRef.current).forEach(layer => {
        layer.setStyle({
          fillOpacity: 0.1,
          weight: 1,
          color: 'rgba(77, 142, 33, 1)',
          fillColor: 'rgba(77, 142, 33, 1)'
        });
      });

   
      polygonLayer.setStyle({
        fillOpacity: 0.3,
        weight: 2,
        color: 'green',
        fillColor: 'green'
      });
      activePolygonRef.current = polygonLayer;
    }
  }, []);

  useEffect(() => {
    if (!map || !polygonsData?.res) return;

    const polygonsArray = Array.isArray(polygonsData.res) ? polygonsData.res : [polygonsData.res];

    Object.values(polygonLayersRef.current).forEach(layer => layer.remove());
    polygonLayersRef.current = {};

    polygonsArray.forEach((polygon) => {
      try {
        const parsedPolygon = JSON.parse(polygon.polygonJson);
        if (!parsedPolygon || !Array.isArray(parsedPolygon) || !parsedPolygon[0]) return;

        const geoJson = parsedPolygon[0].geojson;
        if (!geoJson || !geoJson.coordinates || !Array.isArray(geoJson.coordinates)) return;

        const coordinates = geoJson.type === 'MultiPolygon'
          ? geoJson.coordinates[0][0]
          : geoJson.coordinates[0];

        if (!Array.isArray(coordinates)) return;

        const latLngs: LatLngTuple[] = coordinates.map((coord: number[]) => [coord[1], coord[0]]);

        const polygonLayer = L.polygon(latLngs, {
          color: 'rgba(77, 142, 33, 1)',
          fillColor: 'rgba(77, 142, 33, 1)',
          fillOpacity: 0.1,
          weight: 1,
          className: styles.clickablePolygon
        }).addTo(map);

        polygonLayer.on('click', () => {
          if (polygon.id != null) {
            setActivePolygonStyle(polygonLayer);
            onRegionClick(polygon.id, polygon.nameRu);
          }
        });

        if (polygon.id != null) {
          polygonLayersRef.current[polygon.id.toString()] = polygonLayer;
          
          
          if (initialRegionId && polygon.id === initialRegionId) {
            setActivePolygonStyle(polygonLayer);
          }
        }
      } catch (error) {
        console.error(`Error processing polygon for ${polygon.nameRu}:`, error);
      }
    });

    const polygonLayers = Object.values(polygonLayersRef.current);
    if (polygonLayers.length > 0) {
      const group = L.featureGroup(polygonLayers);
      map.fitBounds(group.getBounds());
      onPolygonLayersInit(polygonLayersRef.current);
    }
  }, [map, polygonsData, onRegionClick, setActivePolygonStyle, initialRegionId]);

  return null;
};

export const MachineryMap: React.FC<MachineryMapProps> = ({
  latitude,
  longitude,
  categoryData,
  typeData,
  isLoading,
  polygonsData,
  onRegionClick,
  selectedRegionName,
  initialRegionId
}) => {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [equipmentDataRight, setEquipmentDataRight] = useState<EquipmentItem[]>([]);
  const mapRef = useRef<Map | null>(null);
  const [polygonLayers, setPolygonLayers] = useState<{ [key: string]: L.Polygon }>({});

  const handleReset = useCallback(() => {
    onRegionClick(0, '');
    Object.values(polygonLayers).forEach((layer: L.Polygon) => {
      layer.setStyle({
        fillOpacity: 0.1,
        weight: 1
      });
    });
  }, [onRegionClick, polygonLayers]);

  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (typeData?.res && Array.isArray(typeData.res)) {
      const mappedData: EquipmentItem[] = typeData.res
        .filter(item => item.transportTypeQuantity > 0)
        .map((item: TransportTypeData) => ({
          name: i18n.language === 'ky'
            ? item.transportType.nameKy || item.transportType.nameRu || 'Неизвестно'
            : item.transportType.nameRu || item.transportType.nameKy || 'Неизвестно',
          count: item.transportTypeQuantity,
          icon: ''
        }));
      setEquipmentDataRight(mappedData);
    }
  }, [typeData]);

  return (
    <div className={styles.container}>
      <div className={styles.mapWrapper}>
        <MapContainer
          center={[latitude, longitude]}
          zoom={6}
          minZoom={6}
          maxZoom={6}
          dragging={false}
          scrollWheelZoom={false}
          doubleClickZoom={false}
          touchZoom={false}
          style={{ height: '100%', width: '100%' }}
          attributionControl={false}
          zoomControl={false}
          ref={mapRef}
        >
     <TileLayer
         attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
         url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
       />
        
          <MapComponent
            polygonsData={polygonsData}
            onRegionClick={onRegionClick}
            onPolygonLayersInit={setPolygonLayers}
            initialRegionId={initialRegionId}
          />
        </MapContainer>

      </div>

      <div className={styles.tableWrapper}>
        <div className={styles.tableHeader}>
          <div className={styles.titleContainer}>
            <h2 className={styles.titleText}>
              {selectedRegionName ? selectedRegionName : 'Общие данные'}
            </h2>
            {selectedRegionName && (
              <button
                className={styles.closeButton}
                onClick={handleReset}
                title="Вернуться к общим данным"
              >
                ✕
              </button>
            )}
          </div>
          <div className={styles.tableHeaderRow}>
            <div>Техника</div>
            <div>Кол-во</div>
          </div>
        </div>
        <div className={styles.tableContent}>
          {isLoading ? (
            <div className={styles.loaderContainer}>
              <div className={styles.loader}></div>
              <div className={styles.loaderText}>Загрузка данных...</div>
            </div>
          ) : (
            equipmentDataRight
              .filter(item => item.name.toLowerCase().includes(searchTerm.toLowerCase()))
              .map((item, index) => (
                <div key={index} className={styles.tableRow}>
                  <div className={styles.techColumn}>
                    <span>{item.icon}</span>
                    <span>{item.name}</span>
                  </div>
                  <div>{item.count}</div>
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  );
};
